import { TableCellTypes, TypographyColors } from '@carboledger/ui';
import {
  CellText,
  makeLinkCell,
  makeUnitTypeCell,
  makeUnitScopedCell,
  makeViewBtnCell,
} from '@/components/common';
import { useTranslations } from '@/hooks';
import { ALLOCATION_STATUS_TONE } from '@/config/statusTones';
import { ALLOCATION_COLUMNS, URGENT_DAYS } from './allocations.constants';
import { buildStatusVariants } from './allocationStatus';
import { buildRowUnitOptions, unitTypeLabel } from './allocationUnits';

// One entry per column, in design order. Nothing is sortable — the route has no sort.

const renderUnitTypeCell = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));
const renderCertifiedQuantity = makeUnitScopedCell('certifiedQuantity');
const hasDeclaration = (row) => row.hasDeclaration;

// Days left: red once the dispatch deadline is inside a week.
const DaysLeftCell = ({ value }) => {
  const t = useTranslations();
  if (value == null) return <CellText color={TypographyColors.secondary}>-</CellText>;
  const color = value < URGENT_DAYS ? TypographyColors.error : TypographyColors.primary;
  return <CellText color={color}>{t('ALLOCATIONS_DAYS_LEFT', { days: value })}</CellText>;
};
const renderDaysLeft = (ctx) => <DaysLeftCell value={ctx.value} />;

// Every column pins, per Figma — the header carries the pin control on all of them.
const textColumn = (id, labelKey, width = 200) => ({
  id,
  header: labelKey,
  type: TableCellTypes.text,
  width,
  pinnable: true,
});

export const buildAllocationsColumns = (t, { unitHeaderSelect, onOpen, onViewDeclaration }) => [
  {
    ...textColumn(ALLOCATION_COLUMNS.transactionId, t('ALLOCATIONS_COL_TRANSACTION_ID'), 160),
    type: TableCellTypes.custom,
    cell: makeLinkCell(onOpen),
  },
  textColumn(ALLOCATION_COLUMNS.declarationNo, t('ALLOCATIONS_COL_DECLARATION_NO'), 160),
  textColumn(ALLOCATION_COLUMNS.materialName, t('FIELD_MATERIAL_NAME')),
  textColumn(ALLOCATION_COLUMNS.facilityName, t('FIELD_FACILITY_NAME'), 143),
  textColumn(ALLOCATION_COLUMNS.terminal, t('FIELD_TERMINAL'), 141),
  {
    ...textColumn(ALLOCATION_COLUMNS.status, t('FIELD_STATUS'), 180),
    type: TableCellTypes.statusDot,
    variantMap: buildStatusVariants(t, ALLOCATION_STATUS_TONE),
  },
  textColumn(ALLOCATION_COLUMNS.internalTransfer, t('ALLOCATIONS_COL_INTERNAL_TRANSFER'), 150),
  textColumn(ALLOCATION_COLUMNS.sendTo, t('ALLOCATIONS_COL_SEND_TO'), 192),
  textColumn(ALLOCATION_COLUMNS.shippingTo, t('ALLOCATIONS_COL_SHIPPING_TO'), 192),
  textColumn(ALLOCATION_COLUMNS.transactionDate, t('ALLOCATIONS_COL_ALLOCATION_DATE'), 150),
  {
    ...textColumn(
      ALLOCATION_COLUMNS.unitType,
      t('FIELD_UNIT_TYPE', { unit: unitTypeLabel(t, unitHeaderSelect.value) }),
      252,
    ),
    type: TableCellTypes.custom,
    cell: renderUnitTypeCell,
    headerSelect: unitHeaderSelect,
  },
  {
    ...textColumn(ALLOCATION_COLUMNS.certifiedQuantity, t('ALLOCATIONS_COL_CERTIFIED_QUANTITY')),
    type: TableCellTypes.custom,
    cell: renderCertifiedQuantity,
  },
  textColumn(ALLOCATION_COLUMNS.massBalancePeriod, t('ALLOCATIONS_COL_MASS_BALANCE_PERIOD'), 260),
  textColumn(ALLOCATION_COLUMNS.reference, t('ALLOCATIONS_COL_REFERENCE'), 180),
  textColumn(
    ALLOCATION_COLUMNS.associatedDeclarationNo,
    t('ALLOCATIONS_COL_ASSOCIATED_DECLARATION_NO'),
    220,
  ),
  {
    ...textColumn(ALLOCATION_COLUMNS.declarations, t('ALLOCATIONS_COL_DECLARATIONS'), 140),
    type: TableCellTypes.custom,
    cell: makeViewBtnCell(onViewDeclaration, hasDeclaration),
  },
  textColumn(ALLOCATION_COLUMNS.transactedBy, t('ALLOCATIONS_COL_TRANSACTED_BY'), 200),
  textColumn(ALLOCATION_COLUMNS.countryOfOrigin, t('FIELD_COUNTRY_OF_ORIGIN')),
  textColumn(ALLOCATION_COLUMNS.rawMaterialType, t('FIELD_RAW_MATERIAL_TYPE')),
  textColumn(ALLOCATION_COLUMNS.ghgEmissions, t('FIELD_GHG_EMISSIONS')),
  textColumn(ALLOCATION_COLUMNS.ghgEmissionSavings, t('ALLOCATIONS_COL_GHG_EMISSION_SAVINGS')),
  textColumn(ALLOCATION_COLUMNS.feedstockCategory, t('FIELD_FEEDSTOCK_CATEGORY')),
  textColumn(ALLOCATION_COLUMNS.allocatedQuota, t('ALLOCATIONS_COL_ALLOCATED_QUOTA')),
  textColumn(ALLOCATION_COLUMNS.dateOfDispatch, t('FIELD_DATE_OF_DISPATCH'), 150),
  {
    ...textColumn(ALLOCATION_COLUMNS.daysLeft, t('FIELD_DAYS_LEFT'), 150),
    type: TableCellTypes.custom,
    cell: renderDaysLeft,
  },
];
